import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Hammer, TreePine, Castle, Calendar, Users, Search, Wrench, CheckCircle, CheckCircle2, Pencil, MessageSquare, Star, MapPin, Quote, Mail, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import heroBgWebp from "@/assets/hero-bg.webp";
import heroBgJpg from "@/assets/hero-bg.jpg";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { revealWords, countUp, prefersReducedMotion } from "@/lib/animations";

gsap.registerPlugin(ScrollTrigger);

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  hammer: Hammer,
  calendar: Calendar,
  castle: Castle,
  users: Users,
  tree: TreePine,
};

const processIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Search, Wrench, Hammer, CheckCircle, CheckCircle2, Pencil, MessageSquare, Calendar, Castle, Users, TreePine,
};

const fallbackProcessSteps = [
  { icon_name: "Search", step_number: "01", title: "Konzultace", description: "Probereme váš projekt, prohlédneme objekt a navrhneme nejlepší postup." },
  { icon_name: "Wrench", step_number: "02", title: "Průzkum a plán", description: "Důkladný stavebně-historický průzkum a příprava detailního plánu prací." },
  { icon_name: "Hammer", step_number: "03", title: "Realizace", description: "Odborná práce tradičními tesařskými technikami s důrazem na kvalitu." },
  { icon_name: "CheckCircle", step_number: "04", title: "Předání", description: "Kontrola kvality, dokumentace a slavnostní předání hotového díla." },
];

const Index = () => {
  const { t, i18n } = useTranslation();
  const isCs = i18n.language?.startsWith("cs");
  const heroRef = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const featuredRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);
  const processLineRef = useRef<SVGPathElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);
  const areasRef = useRef<HTMLDivElement>(null);

  const { data: featuredProjects } = useQuery({
    queryKey: ["featured-projects"],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("*").eq("featured", true).limit(3);
      if (error) throw error;
      return data;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["site-stats"],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_stats").select("*").order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: faqItems } = useQuery({
    queryKey: ["faq-items"],
    queryFn: async () => {
      const { data, error } = await supabase.from("faq_items").select("*").order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: testimonials } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase.from("testimonials").select("*").order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: serviceAreas } = useQuery({
    queryKey: ["service-areas"],
    queryFn: async () => {
      const { data, error } = await supabase.from("service_areas").select("*").order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: woodItems } = useQuery({
    queryKey: ["wood-items-preview"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wood_items")
        .select("id, name, description, dimensions, age, price, images, available, status")
        .eq("available", true)
        .order("created_at", { ascending: false })
        .limit(8);
      if (error) throw error;
      return data;
    },
  });

  const { data: allProjects } = useQuery({
    queryKey: ["projects-count"],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("id").limit(1);
      if (error) throw error;
      return data;
    },
  });

  const hasWood = !!woodItems && woodItems.length > 0;
  const hasProjects = !!allProjects && allProjects.length > 0;

  const { data: heroContent } = useQuery({
    queryKey: ["site-content", "hero"],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_content").select("data").eq("key", "hero").maybeSingle();
      if (error) throw error;
      return (data?.data as any) || null;
    },
  });

  const { data: processStepsData } = useQuery({
    queryKey: ["process-steps"],
    queryFn: async () => {
      const { data, error } = await supabase.from("process_steps").select("*").order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const processSteps = processStepsData && processStepsData.length > 0 ? processStepsData : fallbackProcessSteps;
  const heroTitle = isCs ? (heroContent?.title || "Umělecké Tesařství") : t("hero.title", heroContent?.title || "Umělecké Tesařství");
  const heroSubtitle = isCs ? (heroContent?.subtitle || "Rekonstrukce starobylých památek s úctou k tradičnímu řemeslu. Každý trám má svůj příběh.") : t("hero.subtitle", heroContent?.subtitle || "");
  const heroPrimaryLabel = isCs ? (heroContent?.cta_primary_label || "Naše projekty") : t("hero.cta_projects", heroContent?.cta_primary_label || "Our Projects");
  const heroPrimaryLink = heroContent?.cta_primary_link || "/projekty";
  const heroSecondaryLabel = isCs ? (heroContent?.cta_secondary_label || "Kontaktujte nás") : t("hero.cta_contact", heroContent?.cta_secondary_label || "Contact");
  const heroSecondaryLink = heroContent?.cta_secondary_link || "/kontakt";
  const heroBackground = heroContent?.background_image as string | undefined;

  useEffect(() => {
    const ctxRef = { current: null as gsap.Context | null };
    // Defer ScrollTrigger init to avoid forced reflow during first paint
    const rafId = requestAnimationFrame(() => {
      const ctx = gsap.context(() => {
        const reduced = prefersReducedMotion();

        // Hero parallax background (desktop only for perf)
        const mm = gsap.matchMedia();
        mm.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", () => {
          gsap.to(".hero-bg-img", {
            yPercent: 25, ease: "none", force3D: true,
            scrollTrigger: { trigger: heroRef.current, start: "top top", end: "bottom top", scrub: true, fastScrollEnd: true },
          });
        });

        // Hero title: word-by-word reveal with blur (only in CS — splitting per-word
        // breaks whole-phrase translation, so in other langs fade the whole heading).
        if (heroTitleRef.current && !reduced) {
          if (isCs) {
            revealWords(heroTitleRef.current, { stagger: 0.08, y: 40, start: "top 95%" });
          } else {
            gsap.fromTo(heroTitleRef.current, { y: 40, opacity: 0, filter: "blur(8px)" }, {
              y: 0, opacity: 1, filter: "blur(0px)", duration: 0.8, ease: "power2.out",
            });
          }
        }
        // Subtitle + buttons fade in after title
        if (heroContentRef.current) {
          const subEls = heroContentRef.current.querySelectorAll("p, .hero-cta");
          gsap.fromTo(subEls, { y: 30, opacity: 0 }, {
            y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: "power2.out", delay: 0.5,
          });
        }

        // Hero content fade-out + slight zoom on scroll-down
        if (heroContentRef.current && !reduced) {
          gsap.to(heroContentRef.current, {
            opacity: 0, scale: 1.08, ease: "power2.in",
            scrollTrigger: { trigger: heroRef.current, start: "center top", end: "bottom top", scrub: true },
          });
        }

        // Process timeline: reveal chevron connectors between steps
        if (processRef.current && !reduced) {
          const connectors = processRef.current.querySelectorAll(".process-connector");
          if (connectors.length) {
            gsap.fromTo(
              connectors,
              { opacity: 0, x: -8 },
              {
                opacity: 1, x: 0, duration: 0.5, stagger: 0.15, ease: "power2.out",
                scrollTrigger: { trigger: processRef.current, start: "top 70%" },
              }
            );
          }
        }

        // Counter animation for stats
        if (statsRef.current) {
          const valueEls = statsRef.current.querySelectorAll<HTMLElement>(".stat-value");
          valueEls.forEach((el) => countUp(el));
        }

        // Universal section reveals
        const animateSections = [
          { ref: aboutRef, selector: ".about-item", from: { y: 40, opacity: 0 } },
          { ref: processRef, selector: ".process-step", from: { y: 30, opacity: 0 } },
          { ref: statsRef, selector: ".stat-item", from: { y: 30, opacity: 0 } },
          { ref: featuredRef, selector: ".project-card", from: { y: 50, opacity: 0, rotation: -1 } },
          { ref: testimonialsRef, selector: ".testimonial-card", from: { y: 30, opacity: 0, rotation: -2 } },
          { ref: faqRef, selector: ".faq-animate", from: { y: 30, opacity: 0 } },
          { ref: areasRef, selector: ".area-animate", from: { y: 20, opacity: 0 } },
        ];

        animateSections.forEach(({ ref, selector, from }) => {
          if (ref.current) {
            const els = ref.current.querySelectorAll(selector);
            if (els.length === 0) return;
            gsap.set(els, { willChange: "transform, opacity" });
            gsap.fromTo(els, from, {
              y: 0, opacity: 1, rotation: 0, duration: 0.7, stagger: 0.1, ease: "power2.out", force3D: true,
              scrollTrigger: { trigger: ref.current, start: "top 85%", fastScrollEnd: true },
              onComplete: () => gsap.set(els, { willChange: "auto" }),
            });
          }
        });

        // About cards reveal
        if (cardsRef.current && cardsRef.current.children.length > 0) {
          gsap.fromTo(cardsRef.current.children, { y: 50, opacity: 0 }, {
            y: 0, opacity: 1, duration: 0.7, stagger: 0.15, ease: "power2.out",
            scrollTrigger: { trigger: cardsRef.current, start: "top 85%" },
          });
        }

        // Project card image parallax (desktop only)
        mm.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", () => {
          if (featuredRef.current) {
            const imgs = featuredRef.current.querySelectorAll<HTMLImageElement>(".project-card img");
            imgs.forEach((img) => {
              gsap.to(img, {
                yPercent: -8, ease: "none",
                scrollTrigger: { trigger: img.closest(".project-card"), start: "top bottom", end: "bottom top", scrub: true },
              });
            });
          }
        });

        if (ctaRef.current && ctaRef.current.children.length > 0) {
          gsap.fromTo(ctaRef.current.children, { y: 30, opacity: 0 }, {
            y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "power2.out",
            scrollTrigger: { trigger: ctaRef.current, start: "top 85%" },
          });
        }
      });

      ctxRef.current = ctx;
    });

    return () => {
      cancelAnimationFrame(rafId);
      ctxRef.current?.revert();
    };
  }, []);

  return (
    <div>
      <SEO path="/" />

      {/* JSON-LD: LocalBusiness + areaServed */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Umělecké Tesařství",
          description: "Specializujeme se na rekonstrukce starobylých dřevěných památek. Tradiční tesařské techniky a profesionální přístup.",
          url: "https://umelecke-tesarstvi.cz",
          address: { "@type": "PostalAddress", addressCountry: "CZ" },
          ...(serviceAreas && serviceAreas.length > 0 && {
            areaServed: serviceAreas.map((a) => ({ "@type": "AdministrativeArea", name: a.name })),
          }),
          ...(testimonials && testimonials.length > 0 && {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: (testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length).toFixed(1),
              reviewCount: testimonials.length,
            },
          }),
        }),
      }} />

      {/* JSON-LD: FAQPage */}
      {faqItems && faqItems.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: { "@type": "Answer", text: faq.answer },
            })),
          }),
        }} />
      )}

      {/* Hero */}
      <section id="uvod" ref={heroRef} className="relative h-[calc(100vh-4rem)] flex items-center justify-center bg-primary text-primary-foreground overflow-hidden">
        {heroBackground ? (
          <img src={heroBackground} alt="" className="hero-bg-img absolute inset-0 w-full h-full object-cover opacity-30 scale-110" />
        ) : (
          <picture>
            <source srcSet={heroBgWebp} type="image/webp" />
            <img src={heroBgJpg} alt="" className="hero-bg-img absolute inset-0 w-full h-full object-cover opacity-30 scale-110" width={1920} height={1080} fetchPriority="high" />
          </picture>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-transparent to-primary/70" />
        <div ref={heroContentRef} className="relative z-10 container mx-auto px-4 text-center">
          <h1 ref={heroTitleRef} className="font-serif text-5xl md:text-7xl font-bold mb-6 tracking-tight lg:text-6xl">{heroTitle}</h1>
          <p className="text-lg md:text-2xl max-w-2xl mx-auto mb-10 opacity-90 font-light leading-relaxed">
            {heroSubtitle}
          </p>
          <div className="hero-cta flex flex-col sm:flex-row gap-5 justify-center">
            <Link to={heroPrimaryLink} className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-md overflow-hidden transition-all duration-300 hover:shadow-[0_8px_30px_hsl(var(--accent)/0.4)] hover:scale-105 bg-background text-primary">
              <span className="relative z-10 flex items-center gap-2">{heroPrimaryLabel} <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" /></span>
            </Link>
            <Link
              to={heroSecondaryLink}
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold rounded-md bg-accent text-accent-foreground shadow-lg shadow-accent/30 overflow-hidden transition-all duration-300 hover:shadow-[0_10px_40px_hsl(var(--accent)/0.5)] hover:scale-105"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              <Mail className="h-5 w-5 relative z-10 transition-transform duration-300 group-hover:-rotate-12" />
              <span className="relative z-10">{heroSecondaryLabel}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      {stats && stats.length > 0 && (
        <section className="py-16 bg-secondary text-secondary-foreground border-t border-border" ref={statsRef}>
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => {
                const IconComp = iconMap[stat.icon_name || "hammer"] || Hammer;
                return (
                  <div key={stat.id} className="stat-item text-center">
                    <IconComp className="h-8 w-8 mx-auto mb-3 text-accent opacity-80" />
                    <div className="stat-value font-serif text-3xl md:text-4xl font-bold mb-1 text-secondary-foreground">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Aukce dřeva – náhled */}
      {hasWood && woodItems && woodItems.length > 0 && (
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <h2 className="font-serif text-3xl md:text-5xl font-bold text-foreground mb-2">Aukce dřeva</h2>
                <p className="text-muted-foreground">Vybrané kusy archivního a historického dřeva k dispozici.</p>
              </div>
              <Link to="/archivni-drevo" className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:gap-3 transition-all">
                Zobrazit všechny <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {woodItems.slice(0, 8).map((item: any) => (
                <Link key={item.id} to="/archivni-drevo" className="group">
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1 bg-card border-border h-full">
                    <div className="aspect-square bg-secondary overflow-hidden">
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground"><TreePine className="h-12 w-12" /></div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-serif text-base font-semibold mb-1 text-foreground line-clamp-1">{item.name}</h3>
                      {item.dimensions && <p className="text-xs text-muted-foreground line-clamp-1">{item.dimensions}</p>}
                      {item.price != null && (
                        <p className="mt-2 text-sm font-semibold text-accent">{Number(item.price).toLocaleString("cs-CZ")} Kč</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/archivni-drevo" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-md bg-accent text-accent-foreground hover:shadow-lg transition-all duration-300 hover:scale-105">
                Do aukce dřeva <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}


      {/* About */}
      <section id="o-nas" className="py-24 bg-background text-foreground" ref={aboutRef}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="about-item font-serif text-3xl md:text-5xl font-bold text-foreground mb-6">Tradice a řemeslo</h2>
            <p className="about-item text-muted-foreground text-lg leading-relaxed">
              Specializujeme se na rekonstrukce historických dřevěných konstrukcí. Používáme tradiční tesařské techniky v kombinaci s moderními poznatky, abychom zachovali krásu a autenticitu starobylých památek pro budoucí generace.
            </p>
          </div>
          <div ref={cardsRef} className={`grid grid-cols-1 gap-8 ${hasWood ? "md:grid-cols-3" : "md:grid-cols-2 max-w-4xl mx-auto"}`}>
            {[
              { icon: Castle, title: "Rekonstrukce památek", desc: "Obnova historických krovů, hrázděných staveb a dřevěných konstrukcí.", show: true },
              { icon: Hammer, title: "Tradiční techniky", desc: "Tesařské spoje, ruční opracování a postupy předáváné generacemi.", show: true },
              { icon: TreePine, title: "Aukce dřeva", desc: "Nabízíme staré, ověřené dřevo ideální pro autentické opravy.", show: hasWood },
            ].filter((c) => c.show).map((item, i) => (
              <Card key={i} className="text-center border border-border bg-card hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-default">
                <CardContent className="pt-10 pb-8 px-6">
                  <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-accent/15 flex items-center justify-center">
                    <item.icon className="h-8 w-8 text-accent" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-3 text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Jak pracujeme */}
      <section className="py-24 bg-secondary/50 relative overflow-hidden" ref={processRef}>
        <div className="container mx-auto px-4 relative">
          <h2 className="process-step font-serif text-3xl md:text-5xl font-bold text-center mb-14 text-foreground">Jak pracujeme</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {processSteps.map((step: any, i: number) => {
              const Icon = processIconMap[step.icon_name] || Hammer;
              const isLast = i === processSteps.length - 1;
              return (
                <div key={step.id || i} className="process-step relative text-center">
                  <div className="text-5xl font-serif font-bold text-accent/50 mb-2">{step.step_number}</div>
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent/15 flex items-center justify-center relative z-10 ring-8 ring-secondary/50">
                    <Icon className="h-7 w-7 text-accent" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold mb-2 text-foreground">{step.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>

                  {/* Connector chevron between steps (desktop only) */}
                  {!isLast && (
                    <div
                      aria-hidden="true"
                      className="process-connector hidden lg:flex absolute top-[6.5rem] -right-4 z-0 items-center justify-center text-accent/50"
                    >
                      <ChevronRight className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured projects */}
      {featuredProjects && featuredProjects.length > 0 && (
        <section id="projekty-ukazka" className="py-24 bg-background" ref={featuredRef}>
          <div className="container mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-center mb-14 text-foreground">Vybrané projekty</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredProjects.map((project) => (
                <Link key={project.id} to={`/projekty/${project.id}`} className="project-card">
                  <Card className="overflow-hidden hover:shadow-2xl transition-all duration-500 group hover:-translate-y-2 bg-background">
                    <div className="aspect-[4/3] bg-secondary overflow-hidden">
                      {project.images?.[0] ? (
                        <img src={project.images[0]} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Castle className="h-16 w-16" /></div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-serif text-xl font-semibold mb-2 text-foreground">{project.title}</h3>
                      <p className="text-muted-foreground text-sm line-clamp-2">{project.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/projekty" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-md bg-accent text-accent-foreground hover:shadow-lg transition-all duration-300 hover:scale-105">
                Všechny projekty <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials / Reference */}
      {testimonials && testimonials.length > 0 && (
        <section className="py-24 bg-secondary/50" ref={testimonialsRef}>
          <div className="container mx-auto px-4">
            <h2 className="testimonial-card font-serif text-3xl md:text-5xl font-bold text-center mb-14 text-foreground">Reference</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {testimonials.map((t) => (
                <Card key={t.id} className="testimonial-card bg-background border-border hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <Quote className="h-8 w-8 text-accent/30 mb-3" />
                    <p className="text-muted-foreground leading-relaxed mb-4 italic">"{t.text}"</p>
                    <div className="flex items-center justify-between">
                      <span className="font-serif font-semibold text-foreground">{t.author}</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((r) => (
                          <Star key={r} className={`h-4 w-4 ${r <= t.rating ? "text-[hsl(40,80%,55%)] fill-[hsl(40,80%,55%)]" : "text-muted"}`} />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section id="kontakt-cta" className="py-24 bg-primary text-primary-foreground">
        <div ref={ctaRef} className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6">Máte projekt na rekonstrukci?</h2>
          <p className="text-lg mb-10 opacity-90 max-w-xl mx-auto leading-relaxed">
            Rádi vám pomůžeme s obnovou historických dřevěných konstrukcí. Kontaktujte nás pro nezávaznou konzultaci.
          </p>
          <Link
            to="/kontakt"
            className="group relative inline-flex items-center justify-center gap-3 px-10 py-4 text-lg font-semibold rounded-md bg-accent text-accent-foreground shadow-xl shadow-accent/30 overflow-hidden transition-all duration-300 hover:shadow-[0_12px_45px_hsl(var(--accent)/0.55)] hover:scale-105"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            <Mail className="h-5 w-5 relative z-10 transition-transform duration-300 group-hover:-rotate-12" />
            <span className="relative z-10">Kontaktujte nás</span>
            <ArrowRight className="h-5 w-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* FAQ – nad footrem */}
      {faqItems && faqItems.length > 0 && (
        <section className="py-24 bg-background" ref={faqRef}>
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="faq-animate font-serif text-3xl md:text-5xl font-bold text-center mb-14 text-foreground">Často kladené otázky</h2>
            <Accordion type="single" collapsible className="faq-animate space-y-3">
              {faqItems.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id} className="border border-border rounded-lg px-5 bg-card">
                  <AccordionTrigger className="font-serif text-base font-medium text-foreground hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}
    </div>
  );
};

export default Index;
